import { useNotification } from 'src/hooks/useNotification';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import { Button } from 'src/components/ui/button';
import {
  useCreateKnowledgeBaseMutation,
  useEditKnowledgeBaseNameMutation,
} from 'src/services/knowledgeBaseService';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { useNavigate } from 'react-router-dom';
import type { KnowledgeBase } from 'src/services/knowledgeBaseService';

export const CreateEditAiKnowledgeBaseForm = ({
  close = () => {},
  knowledgeBase,
}: {
  close?: () => void;
  knowledgeBase?: KnowledgeBase;
}) => {
  const notification = useNotification();
  const navigate = useNavigate();

  const [createKnowledgeBase, { isLoading }] = useCreateKnowledgeBaseMutation();

  const [editKnowledgeBase] = useEditKnowledgeBaseNameMutation();

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      name: knowledgeBase?.name || '',
    },
    resolver: yupResolver(
      object({
        name: stringScheme({ required: true })
          .label('Name')
          .max(50, 'The name of your Knowledge Base cannot exceed 50 characters.'),
      }),
    ),
  });

  const submit = (data: { name: string }) => {
    if (knowledgeBase?.id) {
      editKnowledgeBase({
        name: data.name,
        knowledge_base_id: knowledgeBase?.id,
      }).then((editKnowledgeBaseResult) => {
        reset();

        if (!('error' in editKnowledgeBaseResult)) {
          close();
          notification.success('Knowledge Base was successfully updated!');
        }
      });
    } else {
      createKnowledgeBase({
        name: data.name,
      }).then((createKnowledgeBaseResult) => {
        reset();

        if (!('error' in createKnowledgeBaseResult)) {
          navigate(`${PATH_DASHBOARD.AI_KNOWLEDGE_BASE}/${createKnowledgeBaseResult.data.id}`);
          close();
          notification.success('Knowledge Base was successfully created!');
        }
      });
    }
  };

  const submitLabel = knowledgeBase?.id ? 'Save' : 'Create';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(submit)();
      }}
    >
      <div className="content">
        <Controller
          control={control}
          name="name"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={knowledgeBase?.id ? 'New name' : 'Name'}
              placeholder={'AI Knowledge Base'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className="flex justify-center mt-5">
        <div className="flex gap-2.5">
          <Button variant="outline" type="button" className="min-w-24" onClick={close}>
            Cancel
          </Button>
          <Button disabled={!isDirty || isLoading} type="submit" className="min-w-24">
            {isLoading ? 'Loading...' : submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};
